import { ChangeDetectionStrategy, Component, computed, ViewEncapsulation } from '@angular/core';

import { select, Store } from '@ngxs/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MenubarModule } from 'primeng/menubar';
import { MenuItem } from 'primeng/api';

import { AppInfoComponent } from '@shared/components/app-info/app-info.component';
import { OnScreenInfoComponent } from '@shared/components/on-screen-info/on-screen-info.component';
import { UserOptionsComponent } from '@shared/components/user-options/user-options.component';
import { UiActions } from '@store/actions/ui.actions';
import { UiSelectors } from '@store/selectors/ui.selectors';
import { SettingsSelectors } from '@store/selectors/settings.selectors';
import { FeatureflagsSelectors } from '@store/selectors/featureflags.selectors';
import { ButtonModule } from 'primeng/button';

import { CardModule } from 'primeng/card';
import { RouterModule } from '@angular/router';
import { AccessService } from '@services/access.service';
import { BEHEER_PAGE_ROLES, BeheerPage } from '@config/beheer-roles';

@Component({
  selector: 'cito-doe-layout',
  imports: [
    MenubarModule,
    UserOptionsComponent,
    ButtonModule,
    CardModule,
    TranslateModule,
    OnScreenInfoComponent,
    AppInfoComponent,
    RouterModule,
  ],
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutComponent {
  private readonly settingsPeriods = select(SettingsSelectors.settingsPeriods);
  private readonly featureFlags = select(FeatureflagsSelectors.featureFlags);
  readonly infoOnScreenOpen = select(UiSelectors.infoOnScreenOpen);
  readonly appInfoOpen = select(UiSelectors.appInfoOpen);

  readonly menuItems = computed<MenuItem[]>(() => {
    const settingsPeriods = this.settingsPeriods();
    if (!settingsPeriods) {
      return [];
    }

    const canAccessVoorbereidingstoets = this.access.canAccessFeature('voorbereidingstoets');
    const canAccessSchrijfvaardigheid = this.access.canAccessFeature('schrijfvaardigheid');
    const showPlannenDropdown = canAccessVoorbereidingstoets || canAccessSchrijfvaardigheid;

    return [
      {
        id: 'menu-leerlinglijst',
        label: this.translate.instant('MAIN_MENU.LIJST'),
        routerLink: ['/leerlingen'],
        icon: 'pi pi-fw pi-users',
        disabled: false,
      },
      {
        id: 'menu-plannen-doorstroomtoets',
        label: this.translate.instant('MAIN_MENU.PLANNEN'),
        routerLink: ['/plannen'],
        icon: 'pi pi-fw pi-calendar',
        visible: !showPlannenDropdown,
      },
      {
        id: 'menu-plannen',
        label: this.translate.instant('MAIN_MENU.PLANNEN'),
        icon: 'pi pi-fw pi-calendar',
        disabled: false,
        visible: showPlannenDropdown,
        items: this.buildPlannenDropdownItems(canAccessVoorbereidingstoets, canAccessSchrijfvaardigheid),
      },
      {
        id: 'menu-downloads',
        label: this.translate.instant('MAIN_MENU.DOWNLOADS'),
        routerLink: ['/downloads'],
        icon: 'pi pi-fw pi-download',
        disabled: !settingsPeriods['afnamesBeschikbaar']?.withinRange,
        visible: this.access.canAccessFeature('downloads'),
      },
      {
        id: 'menu-rapportage',
        label: this.translate.instant('MAIN_MENU.RAPPORTAGE'),
        routerLink: ['/rapportages'],
        icon: 'pi pi-fw pi-file',
        disabled: !settingsPeriods['rapportagesBeschikbaar']?.withinRange,
      },
      ...this.buildBeheerMenuItems(),
      {
        separator: true,
      },
      {
        id: 'menu-info',
        label: this.translate.instant('MAIN_MENU.INFO'),
        skipLocationChange: true,
        icon: 'pi pi-fw pi-info-circle',
        styleClass: 'app-info-menu-item',
        command: () => this.openAppInfo(),
      },
    ];
  });

  private buildPlannenDropdownItems(
    canAccessVoorbereidingstoets: boolean,
    canAccessSchrijfvaardigheid: boolean,
  ): MenuItem[] {
    const items: MenuItem[] = [
      {
        id: 'menu-plannen-doorstroomtoets',
        label: this.translate.instant('MAIN_MENU.PLANNEN_DOORSTROOMTOETS'),
        routerLink: ['/plannen'],
        icon: 'pi pi-fw pi-file',
      },
    ];
    if (canAccessVoorbereidingstoets) {
      items.push({
        id: 'menu-plannen-voorbereidingstoets',
        label: this.translate.instant('MAIN_MENU.PLANNEN_VOORBEREIDINGSTOETS'),
        routerLink: ['/voorbereidingstoets'],
        icon: 'fa-solid fa-arrow-trend-up',
      });
    }
    if (canAccessSchrijfvaardigheid) {
      items.push({
        id: 'menu-plannen-schrijfvaardigheid',
        label: this.translate.instant('MAIN_MENU.PLANNEN_SCHRIJFVAARDIGHEID'),
        routerLink: ['/schrijfvaardigheid'],
        icon: 'pi pi-fw pi-pencil',
      });
    }
    return items;
  }

  private buildBeheerMenuItems(): MenuItem[] {
    const canAccess = (page: BeheerPage) => this.access.hasAnyRole(BEHEER_PAGE_ROLES[page]);
    const canAccessLeerkrachten = canAccess('leerkrachten');
    const canAccessScholen = canAccess('scholen');
    const canAccessData = canAccess('data');
    const canAccessFeatureflags = canAccess('featureflags');
    const canAccessExport = canAccess('export');
    // Leerkrachten is shown as a standalone link when it is the only accessible beheer page
    const isBeheerDropdownVisible = canAccessScholen || canAccessData || canAccessFeatureflags || canAccessExport;
    const isLeerkrachtenOnlyVisible = canAccessLeerkrachten && !isBeheerDropdownVisible;

    const beheerDropdownItems: MenuItem[] = [
      {
        id: 'menu-beheer-leerkrachten',
        label: this.translate.instant('MAIN_MENU.BEHEER_LEERKRACHTEN'),
        routerLink: ['/beheer/leerkrachten'],
        icon: 'pi pi-fw pi-users',
        visible: canAccessLeerkrachten,
      },
      {
        id: 'menu-beheer-scholen',
        label: this.translate.instant('MAIN_MENU.BEHEER_SCHOLEN'),
        routerLink: ['/beheer/scholen'],
        icon: 'pi pi-fw pi-building',
        visible: canAccessScholen,
      },
      {
        id: 'menu-beheer-periodes',
        label: this.translate.instant('MAIN_MENU.PERIODES_EN_DATA'),
        routerLink: ['/beheer/data'],
        icon: 'pi pi-fw pi-calendar',
        visible: canAccessData,
      },
      {
        id: 'menu-beheer-featureflags',
        label: this.translate.instant('MAIN_MENU.BEHEER_FEATUREFLAGS'),
        routerLink: ['/beheer/featureflags'],
        icon: 'pi pi-fw pi-cog',
        visible: canAccessFeatureflags,
      },
      {
        id: 'menu-beheer-export',
        label: this.translate.instant('MAIN_MENU.BEHEER_EXPORT'),
        routerLink: ['/beheer/export'],
        icon: 'pi pi-fw pi-download',
        visible: canAccessExport,
      },
    ];

    return [
      {
        id: 'menu-beheer',
        label: this.translate.instant('MAIN_MENU.BEHEER'),
        icon: 'pi pi-fw pi-building',
        disabled: false,
        visible: isBeheerDropdownVisible,
        items: beheerDropdownItems,
      },
      {
        id: 'menu-beheer-leerkrachten',
        label: this.translate.instant('MAIN_MENU.BEHEER_LEERKRACHTEN'),
        routerLink: ['/beheer/leerkrachten'],
        icon: 'pi pi-fw pi-users',
        visible: isLeerkrachtenOnlyVisible,
      },
    ];
  }

  get logoPath(): string {
    return this.access.canAccessFeature('downloads') ? '/assets/images/logo.png' : '/assets/images/logo.svg';
  }

  constructor(
    private readonly store: Store,
    private readonly translate: TranslateService,
    private readonly access: AccessService,
  ) {}

  toggleOnScreenInfo() {
    this.store.dispatch(new UiActions.toggleOnScreenInfo());
  }

  openAppInfo() {
    this.store.dispatch(new UiActions.openAppInfo());
  }

  closeAppInfo() {
    this.store.dispatch(new UiActions.closeAppInfo());
  }
}
